/**
 * Reusable multi-select combo box with checkbox dropdown.
 *
 * @module components/MultiSelectComboBox
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Renders a dropdown button that opens a checkbox list for multi-selection.
 *
 * @param {object}   props
 * @param {Array<{value: string, label: string}>} props.options  - Available choices.
 * @param {string[]} props.selected    - Currently selected values.
 * @param {Function} props.onChange    - Called with updated array of selected values.
 * @param {string}   props.placeholder - Text shown when nothing is selected.
 * @param {string}   [props.selectedLabel] - Text shown when items are selected (e.g. "3 selected").
 * @returns {React.ReactElement}
 */
export default function MultiSelectComboBox({ options, selected, onChange, placeholder, selectedLabel }) {
  const [open, setOpen]  = useState(false);
  const containerRef     = useRef(null);

  const handleClickAway = useCallback((e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickAway);
      return () => document.removeEventListener('mousedown', handleClickAway);
    }
  }, [open, handleClickAway]);

  const toggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const label = selected.length > 0
    ? (selectedLabel || `${selected.length} selected`)
    : placeholder;

  return (
    <div className="multi-select-combo" ref={containerRef}>
      <button
        type="button"
        className="multi-select-combo__toggle"
        onClick={() => setOpen(prev => !prev)}
      >
        <span className="multi-select-combo__label">{label}</span>
        <span className="multi-select-combo__arrow">{open ? '\u25B2' : '\u25BC'}</span>
      </button>

      {open && (
        <div className="multi-select-combo__dropdown">
          {options.map(opt => (
            <label key={opt.value} className="multi-select-combo__option">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
